import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Paper,
} from "@mui/material";

const money2 = (v) => {
  const x = Number(v);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
};

const disabledFieldSx = {
  "& .Mui-disabled": {
    backgroundColor: "#f0f0f0 !important",
    color: "#333 !important",
    WebkitTextFillColor: "#333 !important",
  },
};

const deepFindArray = (obj, key, depth = 6) => {
  if (!obj || typeof obj !== "object" || depth <= 0) return [];
  if (Array.isArray(obj[key])) return obj[key];
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === "object") {
      const r = deepFindArray(v, key, depth - 1);
      if (Array.isArray(r) && r.length) return r;
    }
  }
  return [];
};

const deepFindObject = (obj, key, depth = 6) => {
  if (!obj || typeof obj !== "object" || depth <= 0) return null;
  if (obj[key] && typeof obj[key] === "object" && !Array.isArray(obj[key])) return obj[key];
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === "object") {
      const r = deepFindObject(v, key, depth - 1);
      if (r) return r;
    }
  }
  return null;
};

const normalizeTotales = (doc) => {
  const t =
    (doc?.totales && typeof doc.totales === "object" ? doc.totales : null) ||
    deepFindObject(doc, "totales") ||
    {};

  return {
    subtotal_15: money2(t.subtotal_15 ?? 0),
    subtotal_5: money2(t.subtotal_5 ?? 0),
    subtotal_0: money2(t.subtotal_0 ?? 0),
    subtotal_no_objeto: money2(t.subtotal_no_objeto ?? t.subtotal_no_obj ?? 0),
    descuento_total: money2(
      t.descuento_total ??
        t.descuento ??
        0
    ),
    iva_15: money2(t.iva_15 ?? 0),
    iva_5: money2(t.iva_5 ?? 0),
    ice: money2(t.ice ?? 0),
    total: money2(t.total ?? doc?.total ?? 0),
  };
};

const normalizeRow = (d, origen) => {
  if (origen === "COMPRA") {
    const qty = Number(d.cantidad ?? 0);
    const pu = Number(d.costo_unitario ?? 0);
    const base = Number(d.base_imponible ?? qty * pu);
    const descPct = Number(d.descuento_pct ?? 0);
    const descVal = Number(d.descuento_valor ?? 0);
    const ivaPct = Number(d.iva_porcentaje ?? 0);

    const codigo = String(d.codigo ?? "").trim();
    const codigoAux = String(d.codigo_auxiliar ?? "").trim();
    const sku = String(d.sku ?? "").trim();
    const variantType = String(d.variant_type ?? "").trim();
    const variantValue = String(d.variant_value ?? "").trim();
    const descripcion = String(d.descripcion ?? "").trim();

    const partes = [
      codigo,
      codigoAux ? `Aux: ${codigoAux}` : "",
      sku ? `SKU: ${sku}` : "",
      variantType && variantValue ? `${variantType}: ${variantValue}` : "",
      descripcion,
    ].filter(Boolean);

    return {
      cantidad: qty,
      descripcion: partes.join(" - "),
      precio_unit: pu,
      iva_pct: ivaPct,
      desc_pct: descPct,
      desc_val: descVal,
      subtotal: base,
    };
  }

  const pu = Number(d.precio_unitario ?? d.pvp ?? 0);
  const base = Number(d.base_imponible ?? Number(d.cantidad ?? 0) * pu);

  const codigoP = String(d.codigo_principal ?? "").trim();
  const codigoA = String(d.codigo_auxiliar ?? "").trim();
  const detalleExtra = String(d.detalle_adicional ?? "").trim();
  const desc = String(d.descripcion ?? "").trim();

  const full =
    `${codigoP}${codigoA ? ` - ${codigoA}` : ""}`.trim() +
    (desc ? ` - ${desc}` : "") +
    (detalleExtra ? ` - ${detalleExtra}` : "");

  return {
    cantidad: String(d.cantidad ?? ""),
    descripcion: full || desc,
    precio_unit: pu,
    iva_pct: Number(d.iva_porcentaje ?? 0),
    desc_pct: Number(d.descuento_pct ?? 0),
    desc_val: Number(d.descuento_valor ?? 0),
    subtotal: base,
  };
};

const tipoRet = (r) => String(r?.codigo ?? r?.tipo ?? "").trim().toUpperCase();
const codRet = (r) => String(r?.codigo_retencion ?? r?.codigo ?? "").trim();

export default function InvoiceProduct({ doc }) {
  const origen = String(doc?.origen || "").toUpperCase();

  const detalles = useMemo(() => {
    const a = Array.isArray(doc?.detalles) ? doc.detalles : deepFindArray(doc, "detalles");
    return Array.isArray(a) ? a : [];
  }, [doc]);

  const retenciones = useMemo(() => {
    const a = Array.isArray(doc?.retenciones_detalle)
      ? doc.retenciones_detalle
      : deepFindArray(doc, "retenciones_detalle");
    return Array.isArray(a) ? a : [];
  }, [doc]);

  const tot = useMemo(() => normalizeTotales(doc), [doc]);
  const descripcionDoc = (doc?.descripcion || "").toString();

  const rows = useMemo(() => detalles.map((x) => normalizeRow(x, origen)), [detalles, origen]);

  const resumenGlobal = useMemo(() => {
    const ir = retenciones.find((r) => {
      const t = tipoRet(r);
      return t === "1" || t === "IR";
    });

    const iva = retenciones.find((r) => {
      const t = tipoRet(r);
      return t === "2" || t === "IVA";
    });

    return {
      ir: ir ? codRet(ir) : "",
      iva: iva ? codRet(iva) : "",
    };
  }, [retenciones]);

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mt: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1565C0" }}>
              <TableCell sx={{ color: "#fff" }}>Cant.</TableCell>
              <TableCell sx={{ color: "#fff" }}>Descripción</TableCell>
              <TableCell sx={{ color: "#fff" }}>Precio U.</TableCell>
              <TableCell sx={{ color: "#fff" }}>IVA (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. ($)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Ret. IR</TableCell>
              <TableCell sx={{ color: "#fff" }}>Ret. IVA</TableCell>
              <TableCell sx={{ color: "#fff" }}>Subtotal</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>
                  <TextField
                    value={String(r.cantidad ?? "")}
                    disabled
                    size="small"
                    inputProps={{ readOnly: true }}
                    sx={{ width: 50, ...disabledFieldSx }}
                  />
                </TableCell>

                <TableCell>
                  <Typography sx={{ color: "#111827" }}>
                    {String(r.descripcion)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <TextField
                    value={`$${money2(r.precio_unit)}`}
                    disabled
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 90, ...disabledFieldSx }}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    value={String(r.iva_pct ?? 0)}
                    disabled
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 60, ...disabledFieldSx }}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    value={money2(r.desc_pct)}
                    disabled
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 70, ...disabledFieldSx }}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    value={`$${money2(r.desc_val)}`}
                    disabled
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 80, ...disabledFieldSx }}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    value={resumenGlobal.ir || "---"}
                    disabled
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 80, ...disabledFieldSx }}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    value={resumenGlobal.iva || "---"}
                    disabled
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 80, ...disabledFieldSx }}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    value={`$${money2(r.subtotal)}`}
                    disabled
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 90, ...disabledFieldSx }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={8}>
          <Typography sx={{ mb: 1 }}>
            Descripción: <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            value={descripcionDoc}
            multiline
            minRows={3}
            fullWidth
            disabled
            InputProps={{ readOnly: true }}
            sx={disabledFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
            <Grid container spacing={1}>
              {[
                ["Subtotal 15%", tot.subtotal_15],
                ["Subtotal 5%", tot.subtotal_5],
                ["Subtotal 0%", tot.subtotal_0],
                ["Subtotal No Objeto", tot.subtotal_no_objeto],
                ["Descuento", tot.descuento_total],
                ["IVA 15%", tot.iva_15],
                ["IVA 5%", tot.iva_5],
                ["ICE", tot.ice],
                ["Total", tot.total],
              ].map(([label, value], index) => (
                <React.Fragment key={index}>
                  <Grid item xs={7}>
                    <Typography
                      sx={{
                        color: "#333",
                        fontWeight: label === "Total" ? "bold" : "normal",
                      }}
                    >
                      {label}:
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      value={`$${money2(value)}`}
                      fullWidth
                      size="small"
                      disabled
                      sx={disabledFieldSx}
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}